import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';

import { AgentsService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { useSaveAgentOverride } from '@/features/admin/model/useSaveAgentOverride';
import { getAgentOverrides } from '@/features/admin/api/agent-overrides.api';
import { resolveEffectiveAgentMetadata } from '@/features/admin/model/resolveEffectiveAgentMetadata';
import {
  getRegistryAdminToolDescription,
  getRegistryAdminToolName,
} from '@/features/admin/lib/canonicalAdminToolLabels';
import { AgentToolBasicInformation } from '@/features/admin/components/AgentToolBasicInformation';
import { useAgentCategoryOptions } from '@/features/admin/model/useAgentCategoryOptions';
import { useAuth } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useChatStore } from '@/features/chats/stores';
import { useIsCategoryPickerEnabled } from '@/shared/hooks/useIsCategoryPickerEnabled';
import type { CategoryId } from '@/shared/constants/category-colors';

import { ORG_WIDE_SCOPE } from './ExternalTools/constants';
import { WorkspaceScopeTabs } from './ExternalTools/WorkspaceScopeTabs';

export interface AgentBasicInformationSectionProps {
  agentId: string;
}

export function AgentBasicInformationSection({ agentId }: AgentBasicInformationSectionProps) {
  const isCategoryPickerEnabled = useIsCategoryPickerEnabled();
  const categoryOptions = useAgentCategoryOptions();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const [activeScope, setActiveScope] = useState(() => currentWorkspaceId ?? ORG_WIDE_SCOPE);
  const reloadCurrentAgentIfActive = useCallback(async (savedAgentId: string) => {
    if (useChatStore.getState().currentAgentId === savedAgentId) {
      await useChatStore.getState().reloadCurrentAgent();
    }
  }, []);
  const { mutateAsync: saveAgentOverride } = useSaveAgentOverride({ reloadCurrentAgentIfActive });
  const organizationId = useAuth((state) => state.signedInUser.value?.organization.id);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.tools.agents,
    queryFn: async () => {
      const response = await AgentsService.getAllAgents({
        client: inferenceHttp,
        query: { includeNonVisibleAgents: false, showAll: true },
      });
      return response.data ?? [];
    },
  });

  const { data: overrides = [] } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.tools.agents, 'overrides', agentId],
    queryFn: async () => await getAgentOverrides(agentId),
  });

  const agent = agents.find((a) => a.id === agentId);

  const { effectiveName, effectiveDescription, effectiveTag } = useMemo(() => {
    const metadata = resolveEffectiveAgentMetadata(overrides, {
      organizationId,
      workspaceId: activeScope === ORG_WIDE_SCOPE ? undefined : activeScope,
    });
    return {
      effectiveName: metadata.name ?? agent?.name ?? '',
      effectiveDescription: metadata.userFacingDescription ?? agent?.description ?? '',
      // No fallback category. An agent the registry left untagged is in no Tools Hub category,
      // and inventing one here would show a picker claiming it is somewhere it is not.
      effectiveTag: metadata.tag ?? agent?.tag,
    };
  }, [activeScope, overrides, agent, organizationId]);

  const handleSaveBasicInfo = async (
    data: { name: string; description: string; category?: CategoryId },
    workspaceId?: string,
  ) => {
    await saveAgentOverride({
      agentId,
      metadata: {
        name: data.name,
        userFacingDescription: data.description,
        ...(data.category ? { tag: data.category } : {}),
      },
      workspaceId,
      organizationId,
    });
  };

  const workspaces = useAuth((state) => state.signedInUser.value?.workspaces) ?? [];
  const currentWorkspace = workspaces.find((ws) => ws.id === currentWorkspaceId) ?? null;
  const otherWorkspaces = [...workspaces]
    .filter((ws) => ws.id !== currentWorkspaceId)
    .sort((a, b) => a.name.localeCompare(b.name));

  const workspaceIdParam = activeScope === ORG_WIDE_SCOPE ? undefined : activeScope;

  const fallbackName = getRegistryAdminToolName(agentId) || (agent?.name ?? '').trim();
  const fallbackDescription =
    getRegistryAdminToolDescription(agentId) || (agent?.description ?? '').trim();

  const showCategory = isCategoryPickerEnabled && !!effectiveTag;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="size-7" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      <WorkspaceScopeTabs
        activeScope={activeScope}
        onScopeChange={setActiveScope}
        currentWorkspace={currentWorkspace}
        otherWorkspaces={otherWorkspaces}
      />
      <div className="flex flex-col pt-6">
        <div className="flex flex-col rounded-2xl border border-border">
          <AgentToolBasicInformation
            key={workspaceIdParam ?? '__org__'}
            toolName={effectiveName}
            toolDescription={effectiveDescription}
            onSaveBasicInfo={handleSaveBasicInfo}
            workspaceId={workspaceIdParam}
            fallbackName={fallbackName}
            fallbackDescription={fallbackDescription}
            category={showCategory ? effectiveTag : undefined}
            categoryOptions={showCategory ? categoryOptions : undefined}
          />
        </div>
      </div>
    </div>
  );
}
